import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

// --- Types ---

export type TriggerType =
  | 'record_created'
  | 'record_updated'
  | 'stage_changed'
  | 'task_completed'
  | 'deal_won'
  | 'deal_lost'
  | 'renewal_approaching'
  | 'email_received'
  | 'scheduled';

export type ActionType =
  | 'send_email'
  | 'create_task'
  | 'update_field'
  | 'move_stage'
  | 'add_tag'
  | 'remove_tag'
  | 'create_note'
  | 'assign_owner'
  | 'webhook'
  | 'wait';

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'is_empty'
  | 'is_not_empty';

export interface WorkflowCondition {
  field: string;
  operator: ConditionOperator;
  value: string;
}

export interface WorkflowAction {
  id: string;
  type: ActionType;
  config: Record<string, unknown>;
  order: number;
}

export interface WorkflowTrigger {
  type: TriggerType;
  entityType?: string;
  config?: Record<string, unknown>;
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  trigger: WorkflowTrigger;
  conditions: WorkflowCondition[];
  actions: WorkflowAction[];
  runCount: number;
  lastRunAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowHistoryEntry {
  id: string;
  workflowId: string;
  status: 'success' | 'failure' | 'skipped';
  triggeredBy: string;
  entityId?: string;
  entityType?: string;
  actionsExecuted: number;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

export interface WorkflowsResponse {
  workflows: Workflow[];
  total: number;
}

export interface WorkflowHistoryResponse {
  history: WorkflowHistoryEntry[];
  total: number;
}

export interface WorkflowFilters {
  isActive?: boolean;
  triggerType?: TriggerType;
  search?: string;
}

// --- Helpers ---

function buildQueryString(filters?: WorkflowFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
  if (filters.triggerType) params.set('triggerType', filters.triggerType);
  if (filters.search) params.set('search', filters.search);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useWorkflows(filters?: WorkflowFilters) {
  return useQuery({
    queryKey: ['workflows', filters],
    queryFn: () => fetchJson<WorkflowsResponse>(`/api/workflows${buildQueryString(filters)}`),
  });
}

export function useWorkflow(id: string | null) {
  return useQuery({
    queryKey: ['workflow', id],
    queryFn: () => fetchJson<Workflow>(`/api/workflows/${id}`),
    enabled: !!id,
  });
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Workflow>) => postJson<Workflow>('/api/workflows', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useUpdateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Workflow> }) =>
      putJson<Workflow>(`/api/workflows/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', variables.id] });
    },
  });
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/workflows/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useToggleWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => putJson<Workflow>(`/api/workflows/${id}/toggle`, {}),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
    },
  });
}

export function useWorkflowHistory(id: string | null) {
  return useQuery({
    queryKey: ['workflow-history', id],
    queryFn: () => fetchJson<WorkflowHistoryResponse>(`/api/workflows/${id}/history`),
    enabled: !!id,
  });
}
